import {
  aetnaInsurance,
  anthemBCBSInsurance,
  cignaInsurance,
  getInsuranceOptionByDisplayName,
  humanaInsurance,
  medicareInsurance,
  otherInsurance,
  premeraWashingtonInsurance,
} from "@rmh/defs";

/**
 * Logos we actually have artwork for, keyed by payer id. Payers without an
 * entry here (anything Canvas sends that we don't map) fall through to their
 * name as text rather than borrowing another payer's logo.
 *
 * Medicare's asset is the bucket's only SVG. Consumers must load these with
 * CORS (`crossOrigin="anonymous"`) rather than through `next/image`: the
 * optimizer rejects SVG unless `dangerouslyAllowSVG` is set, and the app sends
 * `Cross-Origin-Embedder-Policy: require-corp`, which blocks no-cors
 * cross-origin loads. S3 returns `Access-Control-Allow-Origin: *`, so a CORS
 * fetch satisfies both.
 */
const LOGOS_BY_PAYER_ID: Record<string, { src: string; displayName: string }> = {
  [anthemBCBSInsurance.primaryPayerId]: {
    src: "https://rmh-public.s3.us-east-1.amazonaws.com/images/assets/Anthem-Blue-Cross-Blue-Shield.jpg",
    displayName: anthemBCBSInsurance.displayName,
  },
  [aetnaInsurance.primaryPayerId]: {
    src: "https://rmh-public.s3.us-east-1.amazonaws.com/images/assets/insurance-aetna.png",
    displayName: aetnaInsurance.displayName,
  },
  [cignaInsurance.primaryPayerId]: {
    src: "https://rmh-public.s3.us-east-1.amazonaws.com/images/assets/insurance-cigna.png",
    displayName: cignaInsurance.displayName,
  },
  [premeraWashingtonInsurance.primaryPayerId]: {
    src: "https://rmh-public.s3.us-east-1.amazonaws.com/images/assets/insurance-premera-blue-cross.png",
    displayName: premeraWashingtonInsurance.displayName,
  },
  [medicareInsurance.primaryPayerId]: {
    src: "https://rmh-public.s3.us-east-1.amazonaws.com/images/assets/insurance-medicare-logo.svg",
    displayName: medicareInsurance.displayName,
  },
  // Uploaded without the `insurance-` prefix the other assets use.
  [humanaInsurance.primaryPayerId]: {
    src: "https://rmh-public.s3.us-east-1.amazonaws.com/images/assets/humana.svg",
    displayName: humanaInsurance.displayName,
  },
};

/** Payer names that mean "no payer to bill", not a real carrier. */
const SELF_PAY_NAMES = new Set([
  "self pay",
  "self-pay",
  "selfpay",
  otherInsurance.displayName.toLowerCase(),
]);

export type InsuranceDisplay =
  /** We have artwork for this payer. */
  | { kind: "logo"; src: string; payerName: string }
  /** Patient is paying out of pocket. */
  | { kind: "selfPay" }
  /** A real payer we have no logo for — show the name we were given. */
  | { kind: "payerName"; payerName: string }
  /** Nothing on file, typically an unfinished onboarding. */
  | { kind: "none" };

export interface ResolvableInsurance {
  payerId?: string | null;
  payerName?: string | null;
}

function normalize(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Decides how to present a patient's primary insurance.
 *
 * Resolution is intentionally id-first: `payerId` survives label edits, so a
 * self-pay row is still recognised when someone has retyped `payerName`. Name
 * matching reuses {@link getInsuranceOptionByDisplayName} so Canvas aliases
 * ("CT BCBS", "WA/AK Blue Cross") resolve to the same payer the onboarding
 * autocomplete would pick, instead of being treated as unknown.
 */
export function resolveInsuranceDisplay(
  insurance?: ResolvableInsurance | null
): InsuranceDisplay {
  if (!insurance) {
    return { kind: "none" };
  }

  const payerId = insurance.payerId?.trim();
  const payerName = insurance.payerName?.trim();

  if (
    payerId === otherInsurance.primaryPayerId ||
    (payerName && SELF_PAY_NAMES.has(normalize(payerName)))
  ) {
    return { kind: "selfPay" };
  }

  // The row's own id wins; fall back to resolving the display name so payers
  // that arrive name-only still get their logo.
  const logo =
    (payerId ? LOGOS_BY_PAYER_ID[payerId] : undefined) ??
    (payerName
      ? LOGOS_BY_PAYER_ID[
          getInsuranceOptionByDisplayName(payerName)?.value ?? ""
        ]
      : undefined);

  if (logo) {
    // Prefer the stored name so the label matches the rest of the profile, but
    // fall back to the canonical one for id-only rows.
    return { kind: "logo", src: logo.src, payerName: payerName || logo.displayName };
  }

  if (payerName) {
    return { kind: "payerName", payerName };
  }

  return { kind: "none" };
}
